import {makeAutoObservable, observable} from 'mobx';

import {subscriptionNotificationType} from '../lib/subscriptionNotificationType';
import {
  BumpNotification,
  ChatMessageNotification,
  ChatMessageReactionNotification,
  ComplimentNotification,
  FavouriteNotification,
  LocationVerificationNotification,
  MatchUserOnlineNotification,
  NotificationItem,
  OrdinaryNotification,
  PhotoVerificationNotification,
  SmashUserInvisibleNotification,
  SmashUserVisibleNotification,
  SuperSmashNotification,
  VerificationNotification,
  VisitNotification,
} from './types';

/**
 * Class dedicated to move store messages and manage them from Notifications component
 */
class NotificationStore {
  messages = observable.array<NotificationItem>([], {deep: false});

  constructor() {
    makeAutoObservable(this);
  }

  static generateId(): string {
    return String(Date.now());
  }

  addNotification(
    payload: OrdinaryNotification['payload'],
    id: string = NotificationStore.generateId()
  ) {
    this.add({
      id,
      type: 'ordinary',
      payload,
    } satisfies OrdinaryNotification);
  }

  addComplimentNotification(payload: ComplimentNotification['payload']) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'compliment',
      payload,
    } satisfies ComplimentNotification);
  }

  addVisitNotification(payload: VisitNotification['payload']) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'visit',
      payload,
    } satisfies VisitNotification);
  }

  addFavouriteNotification(payload: FavouriteNotification['payload']) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'favourite',
      payload,
    } satisfies FavouriteNotification);
  }

  addSmashUserVisibleNotification(
    payload: SmashUserVisibleNotification['payload']
  ) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'smashUserVisible',
      payload,
    } satisfies SmashUserVisibleNotification);
  }

  addSmashUserInvisibleNotification(
    payload: SmashUserInvisibleNotification['payload']
  ) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'smashUserInvisible',
      payload,
    } satisfies SmashUserInvisibleNotification);
  }

  addSuperSmashNotification(payload: SuperSmashNotification['payload']) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'superSmash',
      payload,
    } satisfies SuperSmashNotification);
  }

  addBumpNotification(payload: BumpNotification['payload']) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'bump',
      payload,
    } satisfies BumpNotification);
  }

  addVerificationNotification(payload: VerificationNotification['payload']) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'verification',
      payload,
    } satisfies VerificationNotification);
  }

  addChatMessageNotification(payload: ChatMessageNotification['payload']) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'chatMessage',
      payload,
    } satisfies ChatMessageNotification);
  }

  addChatMessageReactionNotification(
    payload: ChatMessageReactionNotification['payload']
  ) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'chatMessageReaction',
      payload,
    } satisfies ChatMessageReactionNotification);
  }

  addPhotoVerificationNotification(
    payload: PhotoVerificationNotification['payload']
  ) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'photoVerification',
      payload,
    } satisfies PhotoVerificationNotification);
  }

  addLocationVerificationNotification(
    payload: LocationVerificationNotification['payload']
  ) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'locationVerification',
      payload,
    } satisfies LocationVerificationNotification);
  }

  addMatchUserOnlineNotification(
    payload: MatchUserOnlineNotification['payload']
  ) {
    this.add({
      id: NotificationStore.generateId(),
      type: 'matchUserOnline',
      payload,
    } satisfies MatchUserOnlineNotification);
  }

  removeMessage(message: NotificationItem) {
    this.messages.remove(message);
  }

  removeNoSubscriptionMessages() {
    this.messages.replace(
      this.messages.filter(
        (message) => !subscriptionNotificationType(message.type)
      )
    );
  }

  clear() {
    this.messages.clear();
  }

  private add(message: NotificationItem) {
    if (this.notificationIds.has(message.id)) {
      return;
    }

    this.messages.push(message);
  }

  get notificationIds() {
    return new Set(this.messages.map((message) => message.id));
  }
}

export default new NotificationStore();
